/**CORE LIBRARIES IMPORT */
import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

/**COMPONENTS IMPORT */
import TurnstileWidget, { ENQUIRY_ACTION } from "../Components/TurnstileWidget";

/**DATA / HOOKS / SERVICES IMPORT */
import { getEventBySlug, formatEventDate, formatPrice } from "../data/events";
import { enquiryTypes, privateOccasions } from "../data/enquiries";
import useSiteSettings from "../hooks/useSiteSettings";
import usePageTitle from "../hooks/usePageTitle";
import { turnstileSiteKey, randomToken } from "../services/booking";
import {
  enquiryAvailable,
  enquiryErrorMessage,
  findEnquiryType,
  localDate,
  submitEnquiry,
  validateEnquiry,
} from "../services/enquiry";
import { fadeUp, stagger } from "../utils/motion";
import { PRIVACY_PATH } from "../data/legal";

const SUCCESS_COPY = {
  table:
    "Thank you, we have received your table request. It is not confirmed yet: we will check availability and contact you to confirm.",
  private: "Thank you, we have received your private event enquiry. We will contact you to talk through the details.",
  catering: "Thank you, we have received your catering enquiry. We will contact you to talk through the details.",
  general: "Thank you, we have received your message and will reply as soon as we can.",
};

const initialForm = (params) => {
  const slug = params.get("event");
  const event = slug ? getEventBySlug(slug) : null;
  const type = params.get("type");
  return {
    type: event ? "general" : enquiryTypes.some((t) => t.value === type) ? type : "table",
    eventSlug: event ? event.slug : null,
    name: "",
    email: "",
    phone: "",
    occasion: "",
    date: "",
    time: "",
    guests: "",
    message: event ? `I have a question about the ${event.title} on ${formatEventDate(event.date)}.` : "",
    website: "", // honeypot: stays empty for people
  };
};

const FieldError = ({ id, error }) => (error ? <p className="form-error" id={id}>{error}</p> : null);

const BookEnquiry = () => {
  usePageTitle("Book / Enquire");
  const [searchParams] = useSearchParams();
  const settings = useSiteSettings();

  const [form, setForm] = useState(() => initialForm(searchParams));
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", error: null, result: null });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  // One key per attempt: retries / double clicks never create a second enquiry.
  const attemptKey = useRef(randomToken());

  const type = findEnquiryType(form.type);
  const has = (f) => type.fields.includes(f);
  const required = (f) => type.required.includes(f);
  const selectedEvent = form.eventSlug ? getEventBySlug(form.eventSlug) : null;
  const available = enquiryAvailable();
  const needsTurnstile = Boolean(turnstileSiteKey);

  const update = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: undefined });
  };
  const err = (f) => errors[f];
  const describedBy = (f) => (err(f) ? `enq-${f}-error` : undefined);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state.status === "sending") return;
    const problems = validateEnquiry(form);
    setErrors(problems);
    if (Object.keys(problems).length) {
      document.getElementById(`enq-${Object.keys(problems)[0]}`)?.focus();
      return;
    }
    if (needsTurnstile && !turnstileToken) {
      setState({ status: "idle", error: "Please complete the security check below the form.", result: null });
      return;
    }
    setState({ status: "sending", error: null, result: null });
    const token = turnstileToken;
    setTurnstileToken(null); // tokens are single-use
    try {
      const result = await submitEnquiry(form, { idempotencyKey: attemptKey.current, turnstileToken: token });
      setState({ status: "sent", error: null, result });
      attemptKey.current = randomToken();
    } catch (error) {
      setState({ status: "idle", error: enquiryErrorMessage(error), result: null });
      if (error.code === "invalid_input" && error.detail) setErrors({ [error.detail]: "Please check this field." });
      setTurnstileReset((n) => n + 1);
    }
  };

  const startAgain = () => {
    setForm(initialForm(new URLSearchParams()));
    setErrors({});
    setState({ status: "idle", error: null, result: null });
    setTurnstileReset((n) => n + 1);
  };

  return (
    <main className="simple-page enquiry-page">
      <motion.div initial="hidden" animate="visible" variants={stagger(0.12)}>
        <motion.p className="page-eyebrow" variants={fadeUp}>Book / Enquire</motion.p>
        <motion.h1 className="page-title" variants={fadeUp}>Book a table or send an enquiry</motion.h1>
        <motion.p className="section-text" variants={fadeUp}>
          Send us your request here and our team will get back to you. A table
          request is not a confirmed booking until we confirm it with you. No
          payment is taken for enquiries.
        </motion.p>
      </motion.div>

      {selectedEvent && (
        <aside className="enquiry-event-summary" aria-label="Selected event">
          {selectedEvent.presentedBy && <p className="page-eyebrow">{selectedEvent.presentedBy}</p>}
          <p className="enquiry-event-title">{selectedEvent.title}</p>
          <p>
            {formatEventDate(selectedEvent.date)}
            {selectedEvent.startTime && <> · {selectedEvent.startTime}</>} · {formatPrice(selectedEvent.price)}{" "}
            {selectedEvent.price.basis} ({selectedEvent.price.note.toLowerCase()})
          </p>
          {selectedEvent.drinks && <p>{selectedEvent.drinks.note}</p>}
          <Link to={`/events/${selectedEvent.slug}`}>View event details</Link>
        </aside>
      )}

      {!available ? (
        <div className="enquiry-sent" role="status">
          <p className="enquiry-event-title">Online enquiries are not available right now</p>
          <p className="section-text">
            Please email us at <a href={`mailto:${settings.email}`}>{settings.email}</a> and we will get back to you.
          </p>
        </div>
      ) : state.status === "sent" ? (
        <div className="enquiry-sent" role="status" aria-live="polite">
          <p className="enquiry-event-title">Request received</p>
          <p className="section-text">{SUCCESS_COPY[type.value]}</p>
          {state.result?.reference && (
            <p className="section-text">
              Your reference: <strong>{state.result.reference}</strong>
            </p>
          )}
          <button type="button" className="hero-btn" onClick={startAgain}>Send another request</button>
        </div>
      ) : (
        <form className="enquiry-form" onSubmit={handleSubmit} noValidate aria-busy={state.status === "sending"}>
          <fieldset className="enquiry-types form-field-full">
            <legend>What would you like to do?</legend>
            {enquiryTypes.map((t) => (
              <label key={t.value} className="enquiry-type">
                <input type="radio" name="type" value={t.value} checked={form.type === t.value} onChange={update} />
                <span>{t.label}</span>
              </label>
            ))}
          </fieldset>

          <div className="form-field">
            <label htmlFor="enq-name">Full name <span aria-hidden="true">*</span></label>
            <input id="enq-name" name="name" type="text" autoComplete="name" required maxLength={120} value={form.name} onChange={update} aria-invalid={Boolean(err("name"))} aria-describedby={describedBy("name")} />
            <FieldError id="enq-name-error" error={err("name")} />
          </div>

          <div className="form-field">
            <label htmlFor="enq-email">Email <span aria-hidden="true">*</span></label>
            <input id="enq-email" name="email" type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={update} aria-invalid={Boolean(err("email"))} aria-describedby={describedBy("email")} />
            <FieldError id="enq-email-error" error={err("email")} />
          </div>

          <div className="form-field">
            <label htmlFor="enq-phone">Phone <span className="form-optional">(optional)</span></label>
            <input id="enq-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} value={form.phone} onChange={update} aria-invalid={Boolean(err("phone"))} aria-describedby={describedBy("phone")} />
            <FieldError id="enq-phone-error" error={err("phone")} />
          </div>

          {has("occasion") && (
            <div className="form-field">
              <label htmlFor="enq-occasion">Occasion <span className="form-optional">(optional)</span></label>
              <select id="enq-occasion" name="occasion" value={form.occasion} onChange={update}>
                <option value="">Choose an occasion</option>
                {privateOccasions.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          )}

          {has("date") && (
            <div className="form-field">
              <label htmlFor="enq-date">
                Preferred date {required("date") ? <span aria-hidden="true">*</span> : <span className="form-optional">(optional)</span>}
              </label>
              <input id="enq-date" name="date" type="date" min={localDate()} required={required("date")} value={form.date} onChange={update} aria-invalid={Boolean(err("date"))} aria-describedby={describedBy("date")} />
              <FieldError id="enq-date-error" error={err("date")} />
            </div>
          )}

          {has("time") && (
            <div className="form-field">
              <label htmlFor="enq-time">
                Preferred time {required("time") ? <span aria-hidden="true">*</span> : <span className="form-optional">(optional)</span>}
              </label>
              <input id="enq-time" name="time" type="time" required={required("time")} value={form.time} onChange={update} aria-invalid={Boolean(err("time"))} aria-describedby={describedBy("time")} />
              <FieldError id="enq-time-error" error={err("time")} />
            </div>
          )}

          {has("guests") && (
            <div className="form-field">
              <label htmlFor="enq-guests">Number of guests <span aria-hidden="true">*</span></label>
              <input id="enq-guests" name="guests" type="number" inputMode="numeric" min="1" max={type.maxGuests} step="1" required value={form.guests} onChange={update} aria-invalid={Boolean(err("guests"))} aria-describedby={describedBy("guests")} />
              <FieldError id="enq-guests-error" error={err("guests")} />
            </div>
          )}

          <div className="form-field form-field-full">
            <label htmlFor="enq-message">
              Message {required("message") ? <span aria-hidden="true">*</span> : <span className="form-optional">(optional)</span>}
            </label>
            <textarea
              id="enq-message"
              name="message"
              rows="5"
              maxLength={2000}
              required={required("message")}
              placeholder={type.value === "general" ? "How can we help?" : "Any questions or requests…"}
              value={form.message}
              onChange={update}
              aria-invalid={Boolean(err("message"))}
              aria-describedby={["enq-message-note", describedBy("message")].filter(Boolean).join(" ")}
            />
            <p className="form-hint" id="enq-message-note">
              Please do not include allergy, health or other sensitive information in your message. If you have
              allergies or dietary requirements, please speak to our staff before ordering or attending.
            </p>
            <FieldError id="enq-message-error" error={err("message")} />
          </div>

          {/* Honeypot: hidden from people and screen readers; bots fill it in. */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="enq-website">Website</label>
            <input id="enq-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update} />
          </div>

          <p className="form-hint form-field-full">
            * Required. We use your details only to answer this request, as explained in our{" "}
            <Link to={PRIVACY_PATH} target="_blank" rel="noopener">Privacy Policy</Link>.
          </p>

          {needsTurnstile && (
            <div className="form-field-full">
              <TurnstileWidget
                siteKey={turnstileSiteKey}
                action={ENQUIRY_ACTION}
                label="Security check (protects our inbox from spam):"
                resetKey={turnstileReset}
                onToken={setTurnstileToken}
              />
            </div>
          )}

          {state.error && <p className="checkout-error form-field-full" role="alert">{state.error}</p>}

          <div className="form-field-full">
            <button type="submit" className="hero-btn hero-btn-active" disabled={state.status === "sending" || (needsTurnstile && !turnstileToken)}>
              {state.status === "sending" ? "Sending…" : "Send Request"}
            </button>
          </div>
        </form>
      )}
    </main>
  );
};

export default BookEnquiry;
